import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import { Button } from '@els/els-react--button';
import InnerHTML from '@Components/InnerHTML';
import DocumentJournals from '@Components/Results/documents/DocumentJournals';
import ListOfAuthors from '@Components/Results/reactions/reaction-details/ListOfAuthors';
import { citationLiteratureFormatter } from '@Services/FormatUtils';
import CitedCountLink from '@Components/Results/CitedCountLink';
import DocumentCustomLink from '@Components/Results/documents/DocumentCustomLink';
import FullText from '@Components/Results/reactions/reaction-details/FullText';
import { trackSurveySubmissionForReferencePreview } from '@Containers/Summary/analytics';
import ThumbsUpDown from '@Containers/Summary/components/ThumbsUpDown';

function ReferenceCitationDetails({ index, citation = {}, onDocumentDetailsClick, metadata, showFeedback = true }) {
  return (
    <>
      <InnerHTML tag="h3" html={citation?.title} className="u-els-font-size-h4 u-els-margin-bottom-1o2" />
      {!isEmpty(citation?.citationJournals) && (
        <DocumentJournals journals={citation.citationJournals} className="u-els-margin-bottom-1o2" />
      )}
      {citation?.citationPatents && (
        <InnerHTML
          tag="div"
          className="rx-element-literature u-els-margin-bottom-1o2"
          html={citationLiteratureFormatter(citation.citationPatents)}
        />
      )}
      {(citation?.assignees || citation?.authors) && (
        <div className="u-els-font-size-meta u-els-margin-bottom-1o2">
          {citation?.assignees && (
            <>
              <span id="current-patent-assignee-label" className="u-els-display-inline-block u-els-margin-right-1o2">
                Current Patent Assignee:
              </span>
              <InnerHTML
                tag="span"
                aria-labelledby="current-patent-assignee-label"
                className="rx-element-assignee u-els-display-inline-block"
                html={citation.assignees + (citation?.authors ? '; ' : '')}
              />
            </>
          )}
          {citation?.authors && <ListOfAuthors metadata={metadata} citation={citation} />}
        </div>
      )}
      {citation?.issueCitedByCounter > 0 && (
        <div className="u-els-margin-bottom-1o2">
          <CitedCountLink
            data-e2e="document-cited-count-link"
            className="e2e-cited-count"
            citedCount={citation.issueCitedByCounter}
            citedUrl={citation.citedBy}
          />
        </div>
      )}
      <div className="u-els-margin-top u-els-margin-bottom o-els-flex-layout o-els-flex-layout--left">
        <Button
          data-e2e="document-details-button"
          type="link"
          onClick={() => onDocumentDetailsClick(citation)}
          aria-label="Document Details – opens the results page for this document"
          className="u-els-margin-right">
          Document Details
        </Button>
        {citation?.fullTextCustom ? (
          <DocumentCustomLink citation={citation} getTrackingAttrs={noop} />
        ) : (
          <FullText
            citation={citation}
            trackingAttributes={{
              index,
              getTrackingAttrs: {},
            }}
            metadata={metadata}
          />
        )}
      </div>
      <h4 className="u-els-margin-bottom">Abstract</h4>
      <InnerHTML tag="p" className="u-els-margin-bottom-2x" html={citation?.abstractText} />
      {showFeedback && (
        <div className="o-els-flex-layout o-els-flex-layout--left">
          <span id={`summary-reference-feedback-${index}`} className="u-els-display-inline-block u-els-margin-right-1x1o2">
            Was this relevant to the question?
          </span>
          <ThumbsUpDown
            thumbsUpProps={{ 'data-e2e': 'summary-reference-like' }}
            thumbsDownProps={{ 'data-e2e': 'summary-reference-dislike' }}
            onFeedbackChange={trackSurveySubmissionForReferencePreview}
            ariaLabelledBy={`summary-reference-feedback-${index}`}
          />
        </div>
      )}
    </>
  );
}

ReferenceCitationDetails.propTypes = {
  index: PropTypes.number,
  citation: PropTypes.object,
  onDocumentDetailsClick: PropTypes.func.isRequired,
  metadata: PropTypes.object,
  showFeedback: PropTypes.bool,
};

export default ReferenceCitationDetails;
